import { IconName } from '../components/ui/Icon';
import {
  Category,
  CategoryKey,
  categories as seedCategories,
  popularServices as seedPopular,
  Service,
  services as seedServices,
} from '../data/services';
import { api } from './client';

/**
 * The catalogue, from the platform rather than from a file.
 *
 * ---------------------------------------------------------------------------
 * THE SERVER DECIDES WHAT IS SOLD
 * ---------------------------------------------------------------------------
 * `src/data/services.ts` was the catalogue: 48 hand-written services that only
 * ever existed inside the app. Two of them matched what the platform actually
 * sells. That is the wrong way round - a price change, a new trade or a service
 * being switched off should not need an app release - so `GET /catalog/categories`
 * is now the source of truth and the seed file is the offline fallback.
 *
 * ---------------------------------------------------------------------------
 * THE SERVER HAS NO PICTURES, AND THE APP IS FULL OF THEM
 * ---------------------------------------------------------------------------
 * The database carries names, prices and durations. It has no artwork, no
 * icons and no ratings, and Home is built out of exactly those: a cutout of a
 * professional per category, a still life per tile, a rating on every card.
 *
 * So each API row is matched back to the seed by name, and takes its pictures
 * from there. A service the app has never seen still renders - `ArtPanel`
 * falls back to the category's glyph - it simply has no photograph.
 *
 * Ratings are NOT invented for an unmatched service. `rating` and `reviews`
 * are left undefined and the card omits the row, because a made-up 4.8 on a
 * service nobody has booked is the one number a customer would be entitled to
 * be angry about.
 */

/**
 * One service, as `GET /catalog/categories` sends it - see `ServiceDto` in
 * the backend's `dto/service.dto.ts`.
 *
 * `flatPrice` is a STRING, not a number: it is a `Decimal(12,2)` column, and
 * Nest serialises a Decimal as the exact string it prints rather than as a
 * float that could round a rupee away. Parsed once, in `toService` below.
 */
type ApiService = {
  id: string;
  categoryId: string;
  name: string;
  description: string | null;
  /**
   * The picture ops uploaded for this service, already loadable.
   *
   * The column stores an object key; the server resolves it before sending -
   * a CDN link where CloudFront is configured, otherwise a signed one that
   * expires. Either way it is a URL this app can hand to `<Image>`, and never
   * a key it would have to sign itself.
   *
   * Null is ordinary: a service nobody has photographed yet. `toService`
   * falls back to the bundled artwork rather than rendering an empty slot.
   */
  imageUrl: string | null;
  /**
   * The two scope lists, as rich text, each in its own column.
   *
   * Not folded into `description` the way the inclusions used to be - ops
   * authors these separately now, in an editor whose toolbar is narrowed to
   * bullet lists precisely because this app throws everything else away.
   *
   * Optional: an older backend answers without them, and a service ops has
   * not written scope for yet sends null. Both mean the same thing here, and
   * `detailFor` falls back to its per-category text for that section alone.
   */
  included?: string | null;
  notIncluded?: string | null;
  /**
   * The sheet's questions, already resolved by the server.
   *
   * Optional for the same two reasons. An empty array is a service nobody has
   * built a sheet for, which is the ordinary state on the day this ships.
   */
  faqs?: { question: string; answer: string }[];
  durationMinutes: number;
  flatPrice: string;
  /**
   * The struck-through figure on the card - "~~₹1,000~~ ₹600".
   *
   * DISPLAY ONLY. Nobody is ever charged it and nothing here may calculate
   * from it: `flatPrice` above is what a booking is made at, and putting this
   * into a cart total or a saving would show a discount that was never given.
   *
   * Null on almost every service, which is the ordinary state - the card
   * shows one price. Always greater than `flatPrice` when set.
   */
  listPrice?: number | null;
  supportsInstant: boolean;
  supportsScheduled: boolean;
  supportsRecurring: boolean;
  isActive: boolean;
  /**
   * How the service is sold, and the hours it can be sold for.
   *
   * Optional for the same reason the scope fields above are: a backend that
   * predates hourly services answers without them, and `toService` reads
   * a missing `pricingModel` as `fixed` - which is what every service
   * on such a backend is.
   *
   * **`flatPrice` means something different on an hourly service**: it is
   * the rate for one hour rather than the price of a job. That is why these
   * three travel together and why no screen may show the price without them.
   */
  pricingModel?: 'fixed' | 'hourly';
  minHours?: number | null;
  maxHours?: number | null;
  /** Units of this service one booking may hold. Optional: an older
      backend answers without it, and the app then assumes 5. */
  maxQuantityPerBooking?: number;
};

/**
 * One node of the browse tree - see `CategoryTreeNodeDto`. Two levels only:
 * a root carries `children`, and a child's own `children` is always empty.
 */
type ApiCategoryNode = {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
  parentCategoryId: string | null;
  /**
   * The category's picture, already loadable - same treatment as
   * `ApiService.imageUrl`.
   *
   * Named `iconUrl` on the server because that is the column, but it is a
   * photograph, not an icon: this app's `Category.icon` is a vector name from
   * its own set and has nothing to do with it. The two live side by side -
   * the picture when there is one, the vector when there is not.
   */
  iconUrl: string | null;
  children: ApiCategoryNode[];
  services: ApiService[];
};

export type Catalogue = {
  categories: Category[];
  /**
   * Every service once, whatever category it came under. What the cart, the
   * search screen and the basket totals read.
   */
  services: Service[];
  /**
   * What to show when a category is opened, keyed by its slug.
   *
   * Held separately rather than derived by filtering `services`, because the
   * two are not the same question. A service belongs to one shelf but appears
   * under its trade as well - "Bathroom Deep Clean" is in `bathroom-cleaning`
   * AND in `home-cleaning` - and a flat list with one `category` field each
   * cannot express that. Filtering was leaving every trade tile empty.
   */
  byCategory: Record<string, Service[]>;
  /** Where this came from, so a screen can say so if it wants to. */
  source: 'server' | 'seed';
};

/**
 * Which of the app's own seed categories a server slug looks like.
 *
 * ---------------------------------------------------------------------------
 * ARTWORK ONLY - IT NO LONGER DECIDES WHAT IS SHOWN
 * ---------------------------------------------------------------------------
 * This map used to be the gate: a slug missing from it was a category the app
 * dropped on the floor, which is how the platform came to hold fourteen
 * categories while Home showed six. Now every category the server returns is
 * rendered, and this is consulted for one thing - whether we happen to have a
 * photograph and an icon for it.
 *
 * A miss is fine and expected. `iconFor` guesses a glyph from the name, and
 * `ArtPanel` already falls back to that glyph when there is no picture.
 *
 * Only `cleaning` and `appliance` have artwork behind them today - the seed
 * file was trimmed to the two categories the platform actually sells
 * (`prisma/seed.ts`), so slugs for trades the platform does not yet offer
 * (plumbing, electrical, carpentry, pest control, salon, wellness) are left
 * out rather than pointed at a seed category that no longer exists.
 */
const SEED_LOOK_BY_SLUG: Record<string, string> = {
  'home-cleaning': 'cleaning',
  'deep-cleaning': 'cleaning',
  'bathroom-cleaning': 'cleaning',
  'appliance-repair': 'appliance',
  'appliance-installation': 'appliance',
  'ac-service': 'appliance',
};

/**
 * A glyph for a category the app has never heard of.
 *
 * Keyword-matched against the name rather than the slug, because the name is
 * what a human wrote and is the likelier place for the word "clean" to appear.
 * `grid` is the honest fallback: a neutral mark rather than a wrong one.
 */
const iconFor = (name: string): IconName => {
  const text = name.toLowerCase();
  const guesses: Array<[RegExp, IconName]> = [
    [/clean|wash|maid|sanit/, 'sprayBottle'],
    [/plumb|tap|pipe|basin|toilet|geyser|water/, 'faucet'],
    [/electric|wir|switch|fan|light|mcb|fuse/, 'plug'],
    [/appliance|ac |air|fridge|refriger|machine|microwave|purifier/, 'washer'],
    [/carpent|furnitur|door|lock|shelf|curtain|hinge/, 'hammer'],
    [/pest|cockroach|termite|rodent/, 'bug'],
    [/salon|beauty|hair|makeup|nail|facial/, 'scissors'],
    [/wellness|spa|massage|yoga|therapy/, 'leaf'],
    [/paint|roller/, 'paintRoller'],
  ];

  const hit = guesses.find(([pattern]) => pattern.test(text));
  return hit ? hit[1] : 'grid';
};

/**
 * Loose name match, so "Bathroom deep cleaning" finds "Bathroom Deep Clean".
 *
 * The same normalisation the server-side seeding script uses. It has to be, or
 * a service seeded from the app would come back and fail to match the row it
 * was created from - which would show as a photograph disappearing the moment
 * the catalogue went live.
 */
const normalise = (name: string): string =>
  name
    .toLowerCase()
    .replace(/\(.*?\)/g, ' ')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\b(cleaning|clean)\b/g, 'clean')
    .replace(/\b(installation|install|fitting)\b/g, 'install')
    .replace(/\b(repair|repairs)\b/g, 'repair')
    .replace(/\b(and|the|at|a|of|for|home)\b/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(' ');

const seedByName = new Map(
  seedServices.map(service => [normalise(service.name), service]),
);
const seedCategoryByKey = new Map(
  seedCategories.map(category => [category.key, category]),
);

/** The artwork the app happens to have for a server category, if any. */
const lookOf = (slug: string): Category | undefined => {
  const key = SEED_LOOK_BY_SLUG[slug];
  return key ? seedCategoryByKey.get(key) : undefined;
};

/**
 * `Service.description` is HTML, and React Native has no renderer for it.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE SERVER ACTUALLY SENDS
 * ---------------------------------------------------------------------------
 *   <p>Sweeping and mopping through every room.</p>
 *   <ul><li>All floors swept</li><li>Mopped with a floor cleaner</li></ul>
 *
 * Two things in one string: a summary sentence and a list of inclusions. Passed
 * straight into a `<Text>` - which is what happened before this function - every
 * tag renders as literal text, so the card read "<p>Sweeping and mopping</p><ul>
 * <li>All floors swept</li>". A `<Text>` draws exactly the characters it is
 * given; there is no HTML in React Native for it to interpret.
 *
 * ---------------------------------------------------------------------------
 * WHY PARSE RATHER THAN STRIP
 * ---------------------------------------------------------------------------
 * Deleting the tags would leave "Sweeping and mopping through every room.All
 * floors sweptMopped with a floor cleaner" - one run-on sentence with the word
 * boundaries gone, because the tags ARE the separators. The structure the
 * server encoded is worth keeping: the paragraph is the blurb, and each `<li>`
 * is a line the detail screen can list.
 *
 * ---------------------------------------------------------------------------
 * A REGEX, AND WHY THAT IS ENOUGH HERE
 * ---------------------------------------------------------------------------
 * Parsing HTML with regular expressions is normally a mistake. It is
 * acceptable for exactly this input: a fragment the platform generates itself
 * from a fixed template, with no attributes, no nesting beyond `ul > li` and
 * no user-authored content. If the catalogue ever accepts rich text from an
 * admin, this needs a real parser rather than a wider regex.
 *
 * Anything unrecognised degrades to plain text with its tags removed, which is
 * ugly but readable - the one outcome this must never produce is the tags
 * themselves.
 */
const decodeEntities = (value: string): string =>
  value
    /* `&amp;` last, so "&amp;lt;" does not become "<". */
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');

const stripTags = (value: string): string =>
  decodeEntities(value.replace(/<[^>]*>/g, '')).replace(/\s+/g, ' ').trim();

/**
 * A scope list out of the rich text an admin typed.
 *
 * ---------------------------------------------------------------------------
 * THE SAME REGEX, AND THE SAME REASON IT IS ENOUGH
 * ---------------------------------------------------------------------------
 * `parseDescription` below explains why parsing HTML with a regular
 * expression is acceptable for this input and nowhere else. The one thing that
 * has changed since that note was written is that this markup IS admin-authored
 * now - so the guard is no longer "the platform generates it" but the editor's
 * toolbar, which offers bullet and numbered lists and nothing else for these
 * two fields, and the server's allow-list behind it.
 *
 * If that toolbar is ever widened, this needs a real parser first. A tag it
 * does not know is stripped rather than shown, which is ugly but safe; the one
 * outcome that must never reach a customer is the tags themselves.
 *
 * ---------------------------------------------------------------------------
 * A PARAGRAPH WITH NO LIST IS STILL AN ANSWER
 * ---------------------------------------------------------------------------
 * Somebody will type a sentence instead of bullets. Returning nothing for that
 * would blank the section and silently fall back to the app's generic copy -
 * so a lone paragraph becomes a single line. It reads slightly oddly under a
 * tick; it is much better than losing what ops wrote.
 */
export const parseScopeList = (html: string | null | undefined): string[] => {
  if (!html) return [];

  const items = Array.from(html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi))
    .map(match => stripTags(match[1]))
    .filter(Boolean);
  if (items.length) return items;

  const only = stripTags(html);
  return only ? [only] : [];
};

export const parseDescription = (
  html: string | null,
): { blurb: string; includes: string[] } => {
  if (!html) {
    return { blurb: '', includes: [] };
  }

  /* Every <li>, in order. `[\s\S]` rather than `.` so an item broken across
     lines is still matched - the server pretty-prints some rows. */
  const includes = Array.from(html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi))
    .map(match => stripTags(match[1]))
    .filter(Boolean);

  /* The lead paragraph. Falls back to whatever sits before the first list, so
     a description with no <p> wrapper still yields a summary. */
  const paragraph = html.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  const blurb = paragraph
    ? stripTags(paragraph[1])
    : stripTags(html.split(/<[uo]l[^>]*>/i)[0]);

  return { blurb, includes };
};

/** Everything the app renders from the seed, minus what only a file can have. */
const toService = (row: ApiService, category: CategoryKey): Service => {
  const seed = seedByName.get(normalise(row.name));
  const described = parseDescription(row.description);

  /*
   * The server's own `included` column wins over the one hidden in the
   * description.
   *
   * Both exist: inclusions used to arrive as the `<ul>` inside `description`,
   * and that is still how the three seeded services carry theirs. The
   * dedicated column is what ops fills in from the console, so it is the more
   * specific answer wherever there is one - and falling back keeps every
   * service that predates the column exactly as it was.
   */
  const included = parseScopeList(row.included);

  return {
    /* The server's id, not the seed's key. It is what a booking will be
       created against, and two ids for one service is how the wrong thing
       gets booked. */
    key: row.id,
    category,
    name: row.name,
    /* The parsed paragraph, not the raw HTML. An empty parse falls back to the
       seed's own line rather than leaving the card blank. */
    blurb: described.blurb || seed?.blurb || '',
    /*
     * Set only when there is something, all three of them.
     *
     * `detailFor` chooses between the server's copy and its own per-category
     * text by asking whether these are empty - so an empty array written here
     * would read as "ops wrote nothing for this section", which is true, and
     * an empty array written where ops DID write something would be a lie.
     * Omitting keeps the two indistinguishable in the one direction that is
     * safe.
     */
    ...(included.length
      ? { includes: included }
      : described.includes.length
        ? { includes: described.includes }
        : {}),
    ...(() => {
      const excludes = parseScopeList(row.notIncluded);
      return excludes.length ? { excludes } : {};
    })(),
    ...(row.faqs?.length ? { faqs: row.faqs } : {}),
    /* A Decimal(12,2), serialised as the exact string it prints - parsed
       once, here, rather than at every screen that reads `price`. */
    price: Number(row.flatPrice),
    /*
     * The struck-through figure, kept only when it is above the price.
     *
     * The server refuses anything lower, so this guard is not distrust of it -
     * it is what stops an older or misconfigured backend producing a card that
     * reads "~~₹500~~ ₹600". Omitted rather than set to null, so the field is
     * absent exactly when there is nothing to strike and the card draws one
     * price. Nothing else in this app may read it: see `Service.wasPrice`.
     */
    ...(row.listPrice != null && row.listPrice > Number(row.flatPrice)
      ? { wasPrice: row.listPrice }
      : {}),
    minutes: row.durationMinutes,
    rating: seed?.rating,
    reviews: seed?.reviews,
    /*
     * The platform's photograph, and the bundled one behind it.
     *
     * The seed artwork is NOT dead weight now that the server has pictures.
     * It covers three ordinary cases: a service ops has not photographed yet,
     * a handset with no connection, and the gap between a service being
     * created and somebody getting round to uploading its picture. A card
     * with a bundled photograph reads as a product; a card with an empty
     * slot reads as a broken app.
     *
     * `{ uri }` and `require()` are both `ImageSourcePropType`, so `ArtPanel`
     * and every other consumer take either without knowing which arrived.
     */
    image: row.imageUrl ? { uri: row.imageUrl } : seed?.image,
    /*
     * Deliberately still the bundled one.
     *
     * `tile` is not a second copy of `image` - it is the trade shot as a still
     * life, tools and products with nobody in frame, because a row of four
     * cut-out portraits reads as a contact list. The server has one image
     * field per service and it holds the portrait, so pointing `tile` at it
     * would quietly replace that composition with four more portraits.
     *
     * When the platform grows a second image per service, this is the line
     * that changes.
     */
    tile: seed?.tile,
    supportsInstant: row.supportsInstant,
    supportsScheduled: row.supportsScheduled,
    supportsRecurring: row.supportsRecurring,
    /*
     * Carried through as sent, with the bounds only where the model asks for
     * them.
     *
     * `hourly` is written explicitly rather than passed straight through,
     * so a backend that grows a third model does not have this app rendering
     * a picker for something it has never heard of. Anything but `hourly`
     * is treated as a fixed-price service, which is the safe reading: one
     * price, no picker, and the price shown is the price charged.
     */
    pricingModel: row.pricingModel === 'hourly' ? 'hourly' : 'fixed',
    ...(row.pricingModel === 'hourly'
      ? {
          minHours: row.minHours ?? 1,
          maxHours: row.maxHours ?? 4,
        }
      : {}),
    /* Carried as sent; `maxPerBooking` reads a missing or unusable one as 5. */
    ...(row.maxQuantityPerBooking === undefined
      ? {}
      : { maxPerBooking: row.maxQuantityPerBooking }),
  };
};

/**
 * A spread worth putting on the "Popular Services" rail.
 *
 * The seed named eight services by key, and those keys do not survive the trip
 * - a live service is identified by the server's uuid. So the same eight are
 * matched back by NAME where the platform sells them, and anything still
 * missing is topped up with the first service of each remaining trade.
 *
 * The top-up matters more than it looks: it is what stops the rail being empty
 * on a catalogue that shares nothing with the seed, which is exactly the state
 * this app was in before the two were reconciled.
 */
export const popularFrom = (services: Service[], limit = 8): Service[] => {
  const wanted = new Set(seedPopular().map(service => normalise(service.name)));
  const picked = services.filter(service =>
    wanted.has(normalise(service.name)),
  );
  const seen = new Set(picked.map(service => service.key));

  /*
   * Then one per trade, so a short rail is a spread rather than six variations
   * of the same job.
   */
  if (picked.length < limit) {
    const perCategory = new Map<CategoryKey, Service>();
    services.forEach(service => {
      if (!seen.has(service.key) && !perCategory.has(service.category)) {
        perCategory.set(service.category, service);
      }
    });
    perCategory.forEach(service => {
      picked.push(service);
      seen.add(service.key);
    });
  }

  /*
   * Then everything else, in catalogue order.
   *
   * Only reached when the caller asks for more than the spread can supply -
   * which is what Home does now, passing the whole catalogue's length to show
   * every service on the rail. With the default limit of 8 this loop adds
   * nothing.
   */
  if (picked.length < limit) {
    services.forEach(service => {
      if (!seen.has(service.key)) {
        picked.push(service);
        seen.add(service.key);
      }
    });
  }

  return picked.slice(0, limit);
};

/**
 * What else to offer beside a basket, out of the LIVE catalogue.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS REPLACES, AND WHY IT WAS THE WORST OF THEM
 * ---------------------------------------------------------------------------
 * The cart's rail was built by `similarServices` in `data/services.ts`, which
 * reads the bundled subcategories, the bundled `popularServiceKeys` and the
 * bundled `services` array - three seed sources and not one live one. So a
 * customer with a real basket was shown cards for services the platform has
 * never sold, at prices nobody set.
 *
 * Every other seed leak was cosmetic beside this one, because these cards are
 * TAPPABLE: the sheet opens on a seed key, and the key a seed service carries
 * is not a uuid, so anything reached from there fails at checkout on an id the
 * server has never heard of. It sat one tap from taking money.
 *
 * ---------------------------------------------------------------------------
 * THE ORDER, WHICH IS THE ONLY REAL DESIGN HERE
 * ---------------------------------------------------------------------------
 * Same category first - a basket with a bathroom clean in it should be offered
 * a kitchen clean, not whatever happens to be first in the catalogue. Then the
 * rest, so the rail is never one lonely card on a small catalogue. Anything
 * already in the basket is skipped, or the rail offers what is sitting above
 * it.
 */
export const suggestFrom = (
  all: Service[],
  inCart: Service[],
  limit = 6,
): Service[] => {
  const have = new Set(inCart.map(service => service.key));
  const wantedCategories = new Set(inCart.map(service => service.category));

  const picked: Service[] = [];
  const take = (service: Service) => {
    if (have.has(service.key) || picked.some(p => p.key === service.key)) {
      return;
    }
    picked.push(service);
  };

  all.forEach(service => {
    if (wantedCategories.has(service.category)) {
      take(service);
    }
  });
  all.forEach(take);

  return picked.slice(0, limit);
};

/**
 * What to rotate through the search field, out of the live catalogue.
 *
 * The bundled `searchHints` are five phrases written when the app sold a
 * different catalogue - "deep cleaning", "AC service" - and a hint is a
 * promise that typing it finds something. Typing those finds nothing, which
 * is a worse first impression than an empty field.
 *
 * Lower-cased because a hint is read as an example of what to type, and the
 * catalogue's own Title Case reads as a heading. Truncated rather than
 * exhaustive: this rotates one at a time, and a customer sees two or three.
 */
export const hintsFrom = (services: Service[], limit = 6): string[] =>
  services.slice(0, limit).map(service => service.name.toLowerCase());

/**
 * The catalogue, or the seed if the platform cannot be reached.
 *
 * Never throws. A customer opening the app on a train does not need an error
 * screen in front of a catalogue that has not changed in a week - they need
 * the catalogue. What they must not get is a *booking* against a stale price,
 * and that is the cart's problem to solve against a live quote, not this
 * function's.
 */
export const fetchCatalogue = async (
  signal?: AbortSignal,
): Promise<Catalogue> => {
  try {
    /*
     * `/catalog/categories`, not `/catalogue` - the backend has never
     * exposed the latter. Calling it 404'd on every launch and silently
     * fell through to the seed catalogue below, which is why a booking
     * could be made against a seed key the server had never heard of.
     */
    const tree = await api.get<ApiCategoryNode[]>('/catalog/categories', {
      anonymous: true,
      signal,
    });

    const categories: Category[] = [];
    const services: Service[] = [];
    const byCategory: Record<string, Service[]> = {};

    /*
     * Two levels, walked flat: a root category AND each of its children
     * become one entry apiece here, the same shape `data/services.ts`'s own
     * `cleaning`/`deep-cleaning`/`bathroom-cleaning` already use - the app's
     * screens have never had a notion of nesting deeper than "a tile, and
     * the shelf you land on after tapping it".
     *
     * A child's services also roll up into its parent's list, so the trade
     * tile shows everything under it and the shelf tile shows just its own
     * - the same "same service, two tiles" behaviour the flat `/catalogue`
     * shape used to give for free via `directCount`.
     */
    const addNode = (node: ApiCategoryNode, rollUpInto?: string) => {
      const look = lookOf(node.slug);

      categories.push({
        key: node.slug,
        label: node.name,
        icon: look?.icon ?? iconFor(node.name),
        /* The platform's picture, with the bundled one behind it - see the
           note on `toService`. */
        image: node.iconUrl ? { uri: node.iconUrl } : look?.image,
        /*
         * The same picture as `image`, not the bundled tile.
         *
         * `tile` used to be `look?.tile` alone, and `look` only resolves for
         * the six slugs in `SEED_LOOK_BY_SLUG` - all of them names the
         * platform stopped using. So every live category came back with no
         * tile at all, and the home grid drew them as empty tinted squares:
         * "Daily Cleaning" and "House Shifting" were blank boxes on the very
         * first screen of the app while the two the map happened to catch had
         * artwork.
         *
         * The category has one photograph in the console, so both slots take
         * it. The bundled tile stays as the fallback for anything the server
         * has no picture for.
         */
        tile: node.iconUrl ? { uri: node.iconUrl } : look?.tile,
      });

      const mapped = node.services.map(service =>
        toService(service, node.slug),
      );
      byCategory[node.slug] = mapped;

      mapped.forEach(service => {
        if (!services.some(existing => existing.key === service.key)) {
          services.push(service);
        }
      });

      if (rollUpInto) {
        const parentList = byCategory[rollUpInto] ?? [];
        mapped.forEach(service => {
          if (!parentList.some(existing => existing.key === service.key)) {
            parentList.push(service);
          }
        });
        byCategory[rollUpInto] = parentList;
      }

      node.children.forEach(child => addNode(child, node.slug));
    };

    tree.forEach(root => addNode(root));

    if (!categories.length) {
      throw new Error('catalogue came back empty');
    }

    return { categories, services, byCategory, source: 'server' };
  } catch {
    /*
     * ---------------------------------------------------------------------
     * NOTHING, AND IT SAYS SO. THIS USED TO ANSWER WITH THE BUNDLE.
     * ---------------------------------------------------------------------
     * The old branch grouped `seedServices` and returned them as the
     * catalogue. On the day it was written the bundle mirrored the platform's
     * three services exactly, and it was a reasonable thing to do.
     *
     * It stopped being reasonable. The platform sells fifteen services now -
     * Brooming & Mopping, Car Wash, Fridge Cleaning, Express Pre-Party - and
     * not one of them is in the bundle. Every service the bundle DOES carry is
     * a draft on the server. So a customer with no signal was shown a shop
     * full of things that cannot be bought, and tapping one led four screens
     * deep to a checkout that failed with "serviceId must be a UUID", because
     * the bundle's ids are keys like `clean-full` and never were uuids.
     *
     * An empty catalogue is worse-looking and better. There is nothing lost by
     * showing nothing: the API is unreachable, so a booking could not be made
     * from those cards anyway. What is gained is that the customer is told the
     * truth at the top of the screen instead of at the bottom of the funnel.
     *
     * `source: 'seed'` is kept as the marker for this state - it is what the
     * screens read to draw "could not load, retry" rather than "no services in
     * your area", and those two are very different sentences.
     */
    return {
      categories: [],
      services: [],
      byCategory: {},
      source: 'seed',
    };
  }
};
